import { forwardRef, useImperativeHandle, useMemo, useRef } from 'react';
import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { Loader } from '@hivebrite/design-system/Loader';
import {
  separatorShadow,
  useTheme,
  zIndices,
  useBreakpoint,
} from '@hivebrite/design-system/Theme';
import { useIsMobileDevice, useScrollToElement } from '@hivebrite/react-hooks';

import useFrameTheme from 'hb-react/shared/hooks/theme/useFrameTheme';
import ThemeProvider from 'hb-react/style/ThemeProvider';

import Modal from '..';
import {
  type ModalWithTemplateProps,
  type ModalWithTemplateRef,
  type WidthProps,
} from '../types';

/**
  - Usage in FO: OK
  - Usage in PO: OK
  - Usage in BO: OK
 */

const ModalWithTemplate = forwardRef<
  ModalWithTemplateRef,
  ModalWithTemplateProps
>(
  (
    {
      center = false,
      children,
      customHeight,
      customZIndex,
      dataTestId,
      enableEscAction = true,
      footer,
      header,
      isFocusOnRef,
      isLoading = false,
      onCloseModal,
      pb,
      pl,
      pr,
      pt,
      screenReadersCloseButtonText,
      width = 'medium',
    },
    ref,
  ) => {
    const modalHeightWrapperRef = useRef<HTMLDivElement>(null);
    const headerRef = useRef<HTMLDivElement>(null);

    const isMobileDevice = useIsMobileDevice();
    const theme = useTheme();
    const frameTheme = useFrameTheme();
    const breakpoint = useBreakpoint();

    const headerWithLoadingFallback = useMemo(
      () => (isLoading ? __('Loading...') : header),
      [isLoading, header],
    );

    /** Share functions via ref, useful for virtualized list for example */
    useImperativeHandle(ref, () => ({
      getHeaderElement: () => headerRef.current,
      getHeaderHeight: () => headerRef.current?.offsetHeight,
      getMaxModalHeight: () => {
        if (headerRef.current) {
          const windowHeight = window.innerHeight;
          const headerHeight = headerRef.current.offsetHeight;
          const paddingBottom = theme.space[pb ?? 6];

          return Math.floor(
            isMobileDevice
              ? windowHeight - headerHeight - paddingBottom
              : (windowHeight / 100) * 90 - headerHeight - paddingBottom,
          );
        }
      },
      getModalElement: () => modalHeightWrapperRef.current,
      getModalHeight: () => modalHeightWrapperRef.current?.offsetHeight,
    }));

    useScrollToElement(headerRef, {
      behavior: 'auto',
      dependencies: [!isLoading],
    });

    return (
      <Modal
        center={center}
        customHeight={customHeight}
        customZIndex={customZIndex}
        dataTestId={dataTestId}
        enableEscAction={enableEscAction}
        isFocusOnRef={isFocusOnRef}
        onCloseModal={onCloseModal}
        screenReadersCloseButtonText={screenReadersCloseButtonText}
        withTemplate
      >
        {({ closeButton, labelledById, viewportSize, isScrollDown }) => (
          <Wrapper
            bg={frameTheme.backgroundColor}
            minHeight={{ _: '100%', m: 'auto' }}
            ref={modalHeightWrapperRef}
            width={breakpoint === 's' ? '100%' : width}
          >
            <ThemeProvider
              fontBrightness={frameTheme.fontBrightness}
              theme={frameTheme.themeName}
            >
              <HeaderWrapper
                $withShadow={
                  isScrollDown &&
                  (modalHeightWrapperRef.current as HTMLDivElement)
                    ?.offsetHeight > (viewportSize as DOMRect)?.height
                }
                bg={frameTheme.backgroundColor}
              >
                {closeButton}
                {headerWithLoadingFallback ? (
                  <Header id={labelledById} ref={headerRef}>
                    {headerWithLoadingFallback}
                  </Header>
                ) : null}
              </HeaderWrapper>
              <Box
                height={{ _: '100%', m: 'auto' }}
                pb={pb ?? 6}
                pl={pl ?? { _: 4, m: 6 }}
                pr={pr ?? { _: 4, m: 6 }}
                pt={pt ?? 0}
              >
                {isLoading ? (
                  <Box justifyContent="center" my={18} width="100%">
                    <Loader size={72} />
                  </Box>
                ) : (
                  children
                )}
              </Box>
              {footer && !isLoading ? (
                <Footer
                  $withShadow={
                    (modalHeightWrapperRef.current as HTMLDivElement)
                      ?.offsetHeight > (viewportSize as DOMRect)?.height
                  }
                  bg={frameTheme.backgroundColor}
                  data-testid="modal-footer"
                >
                  {footer}
                </Footer>
              ) : null}
            </ThemeProvider>
          </Wrapper>
        )}
      </Modal>
    );
  },
);

const getWidth = (width: WidthProps) => {
  switch (true) {
    case typeof width === 'object':
    case /(%|px)$/.test(`${width}`):
      return width;
    case width === 'large':
      return 808;
    case width === 'medium':
      return 676;
    default:
      return 442;
  }
};

export default ModalWithTemplate;

const Wrapper = styled(Box).attrs<{ width: WidthProps }>(({ width }) => ({
  maxWidth: getWidth(width),
  width: getWidth(width),
}))`
  display: block;
  position: relative;
`;

const Header = styled(Box).attrs({
  as: 'h2',
  font: 'h3-semibold',
  pl: { _: 4, m: 6 },
  pr: 16,
  py: 6,
})``;

const Footer = styled(Box).attrs({
  p: 6,
})<{ $withShadow?: boolean }>`
  bottom: 0;
  position: sticky;
  z-index: ${zIndices.modal + 1};
  justify-content: flex-end;

  ${({ $withShadow }) => $withShadow && separatorShadow}
`;

const HeaderWrapper = styled(Box)<{ $withShadow?: boolean }>`
  top: 0;
  position: sticky;
  z-index: ${zIndices.modal + 1};

  ${({ $withShadow }) => $withShadow && separatorShadow}
  ${({ $withShadow }) => $withShadow && 'margin-bottom: 24px;'}
`;
